import { useRef } from 'react';
import { leftBounds, resizeAt, type ResizableColumn } from './column-width';

/** One keyboard nudge, as a fraction of the section width. */
const KEYBOARD_STEP = 0.01;

interface Props<T extends ResizableColumn> {
  sectionKey: string;
  sectionLabel: string;
  columns: T[];
  canEdit: boolean;
  onChange: (columns: T[]) => void;
}

const percent = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`;

/**
 * Visual column-width editor for one report section (B6.5a-3).
 *
 * Renders the section's columns as proportional strips separated by draggable
 * dividers. Dragging a divider moves width between the two columns it sits
 * between and no others, so the section total is invariant by construction.
 *
 * Everything structural comes from the API: how many columns exist, how wide
 * they are, and how narrow they may become. Nothing here knows which sections
 * are resizable or what the renderer's geometry is — a section is editable
 * because the server said `resizable`, not because of its key.
 *
 * This is a STRUCTURAL editor, not a preview: the strips show proportion, not
 * what the PDF will look like. The generated PDF remains the visual truth.
 *
 * Generic in the column type since B6.9e, so the SE report composer and the MSE
 * template designer share one resizer instead of maintaining two. Both API
 * shapes satisfy `ResizableColumn`; the caller's own type flows through
 * `onChange` unchanged, so neither loses a field.
 */
export function SectionWidthEditor<T extends ResizableColumn>({
  sectionKey,
  sectionLabel,
  columns,
  canEdit,
  onChange,
}: Props<T>) {
  const trackRef = useRef<HTMLDivElement>(null);

  /** Fraction-per-pixel for the current rendered width of the track. */
  const fractionPerPixel = (): number => {
    const width = trackRef.current?.getBoundingClientRect().width ?? 0;
    return width > 0 ? 1 / width : 0;
  };

  const handlePointerDown = (index: number) => (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!canEdit) return;
    // Keep the interaction to the divider: without this the surrounding
    // sortable row could interpret the gesture as the start of a section drag.
    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget;
    const startX = event.clientX;
    const perPixel = fractionPerPixel();
    if (perPixel === 0) return;
    const startColumns = columns;
    handle.setPointerCapture(event.pointerId);

    const move = (e: PointerEvent) => {
      onChange(resizeAt(startColumns, index, (e.clientX - startX) * perPixel));
    };
    const end = () => {
      handle.releasePointerCapture(event.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!canEdit) return;
    const step =
      event.key === 'ArrowLeft' ? -KEYBOARD_STEP : event.key === 'ArrowRight' ? KEYBOARD_STEP : 0;
    if (step === 0) return;
    // Arrow keys are also how @dnd-kit moves a lifted section, so the divider
    // must consume them or a resize would reorder the report instead.
    event.preventDefault();
    event.stopPropagation();
    onChange(resizeAt(columns, index, step));
  };

  return (
    <div className="mt-2" data-testid={`section-width-editor-${sectionKey}`}>
      <div
        ref={trackRef}
        // select-none: a grab that misses the handle, and the drag itself, must
        // never start a text selection across the row (B6.5 UAT defect D-1).
        className="flex w-full select-none items-stretch overflow-hidden rounded border border-border bg-muted"
      >
        {columns.map((column, i) => (
          <div
            key={column.position}
            className="flex min-w-0 items-center justify-center py-1.5"
            style={{ flexBasis: `${column.widthFraction * 100}%` }}
            data-testid={`section-column-${sectionKey}-${column.position}`}
            data-width-fraction={column.widthFraction}
          >
            {/*
              pointer-events-none: the label is decoration. The handle of the
              PREVIOUS column overhangs into this column by half its width, so a
              hit-testable label here would swallow pointerdown over the right
              half of that handle — which is exactly what defect D-1 was.
            */}
            <span className="pointer-events-none truncate px-1 text-[10px] tabular-nums text-muted-foreground">
              {percent(column.widthFraction)}
            </span>

            {i < columns.length - 1 &&
              (canEdit ? (
                <button
                  type="button"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize ${sectionLabel} columns ${column.position + 1} and ${column.position + 2}`}
                  aria-valuenow={Number(percent(column.widthFraction).replace('%', ''))}
                  aria-valuemin={Number((leftBounds(column, columns[i + 1]!).min * 100).toFixed(1))}
                  aria-valuemax={Number((leftBounds(column, columns[i + 1]!).max * 100).toFixed(1))}
                  // relative z-10: the handle is centred on the column boundary
                  // by a negative right margin, so its right half overhangs into
                  // the NEXT column — a later sibling, which would otherwise
                  // paint (and hit-test) on top of it. Positioning the handle
                  // lifts it above that sibling so the whole 12px is grabbable,
                  // without moving it by a single pixel.
                  className="relative z-10 -mr-1.5 ml-auto h-6 w-3 shrink-0 cursor-col-resize touch-none rounded-sm border-x border-border bg-card hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                  data-testid={`resize-handle-${sectionKey}-${column.position}`}
                  onPointerDown={handlePointerDown(i)}
                  onKeyDown={handleKeyDown(i)}
                />
              ) : (
                // Read-only: a plain rule so the structure is still legible, with
                // no handle, no cursor and nothing focusable.
                <span
                  aria-hidden="true"
                  className="-mr-px ml-auto h-6 w-px shrink-0 bg-border"
                  data-testid={`section-divider-${sectionKey}-${column.position}`}
                />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
